import { z } from "zod";
import {
	type FacilityType,
	type SubscriptionTier,
	subscriptionTierSchema,
} from "./clinic.ts";

/**
 * Subscription plan catalog — the single source of truth for what each tier
 * costs and includes. Keyed by the EXISTING tier enum (no schema change):
 * pro = أساسي, premium = احترافي, enterprise = متقدّم, trial = free entry.
 *
 * Prices are the recommended anchors (a per-clinic `monthly_price` still
 * overrides for negotiated deals). Entitlements DOCUMENT each tier and set the
 * AI cost ceiling; enforcing seats/voice on EXISTING clinics is intentionally
 * NOT retroactive (that would cut live clinics' access) — it applies to new
 * signups + when an operator opts in.
 */

/** Paid tiers in ladder order (trial is the free entry, not sold). */
export const PAID_TIER_ORDER = ["pro", "premium", "enterprise"] as const;

export const TIER_LABEL_AR: Record<SubscriptionTier, string> = {
	trial: "تجربة",
	pro: "أساسي",
	premium: "احترافي",
	enterprise: "متقدّم",
};

/** "أساسي" (pro) monthly price in IQD per facility type — the ladder base. */
const BASE_PRICE_IQD: Record<FacilityType, number> = {
	clinic: 50_000,
	lab: 75_000,
	pharmacy: 100_000,
	radiology: 100_000,
};

/** Ladder multipliers off the base price. pro ×1 · premium ×2 · enterprise ×3.5. */
const TIER_MULTIPLIER: Record<SubscriptionTier, number> = {
	trial: 0,
	pro: 1,
	premium: 2,
	enterprise: 3.5,
};

const round5k = (n: number): number => Math.round(n / 5_000) * 5_000;

export interface PlanEntitlements {
	/** Provider seats included (owner + doctors). */
	maxDoctors: number;
	/** Monthly Claude token allowance — the per-clinic AI cost ceiling (F4.8). */
	aiMonthlyTokenBudget: number;
	/** Voice assistant included in the tier. */
	includesVoice: boolean;
}

export const TIER_ENTITLEMENTS: Record<SubscriptionTier, PlanEntitlements> = {
	trial: { maxDoctors: 2, aiMonthlyTokenBudget: 200_000, includesVoice: true },
	pro: { maxDoctors: 2, aiMonthlyTokenBudget: 500_000, includesVoice: false },
	premium: {
		maxDoctors: 5,
		aiMonthlyTokenBudget: 2_500_000,
		includesVoice: true,
	},
	enterprise: {
		maxDoctors: 999,
		aiMonthlyTokenBudget: 10_000_000,
		includesVoice: true,
	},
};

/** Short feature bullets per tier (ar) for the comparison table. */
export const TIER_FEATURES_AR: Record<SubscriptionTier, string[]> = {
	trial: ["كل الميزات ٢١ يوم", "بلا بطاقة"],
	pro: [
		"طبيبان",
		"السجل الطبي + الحجز + الفوترة",
		"بوابة المريض",
		"مساعد ذكاء اصطناعي محدود",
	],
	premium: [
		"حتى ٥ أطباء",
		"كل ميزات أساسي",
		"ذكاء اصطناعي سخي + مساعد صوتي",
		"تحليلات + دعم أولوية",
	],
	enterprise: [
		"أطباء غير محدودين",
		"كل ميزات احترافي",
		"ذكاء اصطناعي/صوت مرتفع",
		"متعدد فروع + تهيئة مخصّصة",
	],
};

/**
 * Monthly price (IQD) for a tier at a facility type. A per-clinic `monthly_price`
 * override replaces the facility BASE (the "أساسي"/pro anchor) and is then scaled
 * by the tier multiplier — so a negotiated clinic still gets a real ladder
 * (override = pro price, ×2 premium, ×3.5 enterprise) and the owner cards, the
 * price hint, and the admin expected_amount all agree.
 */
export function planPriceIqd(
	tier: SubscriptionTier,
	facilityType: string,
	clinicOverride?: string | number | null,
): number {
	let base =
		BASE_PRICE_IQD[facilityType as FacilityType] ?? BASE_PRICE_IQD.clinic;
	if (clinicOverride != null && clinicOverride !== "") {
		const n = Number(clinicOverride);
		if (Number.isFinite(n) && n > 0) base = n;
	}
	return round5k(base * (TIER_MULTIPLIER[tier] ?? 1));
}

export const planCardSchema = z.object({
	tier: subscriptionTierSchema,
	label_ar: z.string(),
	price_iqd: z.number(),
	max_doctors: z.number(),
	ai_monthly_token_budget: z.number(),
	includes_voice: z.boolean(),
	features_ar: z.array(z.string()),
});
export type PlanCard = z.infer<typeof planCardSchema>;

export function planCard(
	tier: SubscriptionTier,
	facilityType: string,
	clinicOverride?: string | number | null,
): PlanCard {
	const e = TIER_ENTITLEMENTS[tier];
	return {
		tier,
		label_ar: TIER_LABEL_AR[tier],
		price_iqd: planPriceIqd(tier, facilityType, clinicOverride),
		max_doctors: e.maxDoctors,
		ai_monthly_token_budget: e.aiMonthlyTokenBudget,
		includes_voice: e.includesVoice,
		features_ar: TIER_FEATURES_AR[tier],
	};
}

/** The 3 paid plan cards for a facility type (for the owner comparison table).
 *  Pass the clinic's `monthly_price` override so the cards match the price the
 *  admin will expect on approval. */
export function paidPlansForFacility(
	facilityType: string,
	clinicOverride?: string | number | null,
): PlanCard[] {
	return PAID_TIER_ORDER.map((t) => planCard(t, facilityType, clinicOverride));
}
